import React, { useEffect, useState } from 'react';
import './index.css';
import API from '../api';
import { FaBars, FaUserCircle, FaSignOutAlt, FaEdit } from 'react-icons/fa';
import Services from './service';
import Employees from './employee';
import { useNavigate, useLocation } from 'react-router-dom';
import AllBookingsPage from './calendar';
import Dashboard from './dashboard';
import Revenue from './Revenue';
import Products from './Products';
import Contacts from './Contacts';
import ManualSale from './ManualSale';
import InvoicePage from './InvoicePage';
import InvoiceList from './InvoiceList';
import logo from '../assets/merchant-marvels.jpg';
import Sidebar from '../components/sidebar';
import EmployeeWorkingHours from './EmployeeWorkingHours.jsx';
import Settings from './Settings';
import Profile from './Profile';

/* ----------------------------------------------------------------
   URL ↔ view mappings
---------------------------------------------------------------- */
const VIEW_TO_PATH = {
  dashboard:            '/dashboard',
  calendar:             '/calendar',
  services:             '/services',
  Products:             '/products',
  contacts:             '/contacts',
  manualSale:           '/manual-sale',
  invoiceList:          '/invoices',
  team:                 '/team',
  Revenue:              '/revenue',
  EmployeeWorkingHours: '/working-hours',
  profile:              '/profile',
  settings:             '/settings',
};

const PATH_TO_VIEW = {
  '/dashboard':     'dashboard',
  '/calendar':      'calendar',
  '/services':      'services',
  '/products':      'Products',
  '/contacts':      'contacts',
  '/manual-sale':   'manualSale',
  '/invoices':      'invoiceList',
  '/team':          'team',
  '/revenue':       'Revenue',
  '/working-hours': 'EmployeeWorkingHours',
  '/profile':       'profile',
  '/settings':      'settings',
};

const PAGE_TITLES = {
  dashboard:            'Dashboard',
  calendar:             'Calendar',
  services:             'Services',
  Products:             'Products',
  contacts:             'Contacts',
  manualSale:           'Manual Sale',
  team:                 'Team',
  Revenue:              'Revenue',
  EmployeeWorkingHours: 'Working Hours',
  profile:              'My Profile',
  settings:             'Settings',
  Invoice:              'Invoice',
  invoiceList:          'Invoices',
};

function Index() {
  const [user, setUser]                             = useState(null);
  const [showUserDetails, setShowUserDetails]       = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const navigate                                    = useNavigate();
  const location                                    = useLocation();
  const [needsBusinessDetail, setNeedsBusinessDetail] = useState(false);
  const [calendarViewMode, setCalendarViewMode]     = useState('month');
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(null);
  const [gcalMessage, setGcalMessage]               = useState(null);

  /* Derive current view + invoice ID from the URL — no state needed */
  const invoiceMatch  = location.pathname.match(/^\/invoices\/(.+)$/);
  const activeView    = invoiceMatch
    ? 'Invoice'
    : (PATH_TO_VIEW[location.pathname] || 'dashboard');
  const selectedInvoiceId = invoiceMatch ? invoiceMatch[1] : null;

  /* ---------- bootstrap ---------- */
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const u = JSON.parse(storedUser);
      setUser(u);
      localStorage.setItem('role', u.role);
      localStorage.setItem('id', u.role === 'admin' ? u.id : u.business_id);
    }

    // Google Calendar OAuth callback — ?gcal=connected|error
    const params = new URLSearchParams(window.location.search);
    const gcal   = params.get('gcal');
    if (gcal === 'connected') {
      setGcalMessage({ type: 'success', text: 'Google Calendar connected successfully!' });
      navigate('/profile', { replace: true });
    } else if (gcal === 'error') {
      setGcalMessage({ type: 'error', text: 'Failed to connect Google Calendar. Please try again.' });
      navigate('/profile', { replace: true });
    }
  }, []);

  useEffect(() => {
    const check = async () => {
      const storedUser = JSON.parse(localStorage.getItem('user'));
      if (!storedUser) return;
      const businessId = storedUser.role === 'admin' ? storedUser.id : storedUser.business_id;
      try {
        await API.get(`/auth/business-detail?businessId=${businessId}`);
      } catch (err) {
        if (err.response?.status === 404) setNeedsBusinessDetail(true);
      }
    };
    check();
  }, []);

  /* ---------- handlers ---------- */
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleNavigateView = (view) => {
    setShowUserDetails(false);
    navigate(VIEW_TO_PATH[view] || '/dashboard');
  };

  /* ---------- view renderer ---------- */
  const renderView = () => {
    switch (activeView) {
      case 'services':             return <Services />;
      case 'team':                 return <Employees />;
      case 'settings':             return <Settings />;
      case 'profile':              return <Profile user={user} />;
      case 'EmployeeWorkingHours': return <EmployeeWorkingHours />;
      case 'calendar':
        return (
          <AllBookingsPage
            initialView={calendarViewMode}
            initialDate={calendarSelectedDate}
          />
        );
      case 'Revenue':    return <Revenue />;
      case 'Products':   return <Products />;
      case 'contacts':   return <Contacts />;
      case 'manualSale': return <ManualSale />;
      case 'invoiceList':
        return (
          <InvoiceList
            user={user}
            onOpenInvoice={(id) => navigate(`/invoices/${id}`)}
          />
        );
      case 'Invoice':
        return <InvoicePage createdInvoiceId={selectedInvoiceId} />;
      case 'dashboard':
      default:
        return (
          <Dashboard
            onOpenCalendarDay={(date) => {
              setCalendarViewMode('day');
              setCalendarSelectedDate(date || new Date());
              navigate('/calendar');
            }}
            onOpenCalendarMonth={() => {
              setCalendarViewMode('month');
              setCalendarSelectedDate(new Date());
              navigate('/calendar');
            }}
            onOpenRevenue={() => navigate('/revenue')}
            onOpenInvoice={(invoiceId) => navigate(`/invoices/${invoiceId}`)}
          />
        );
    }
  };

  const userInitial = user?.name?.[0]?.toUpperCase() || 'U';

  return (
    <div className="dashboard-container">

      {/* ===== TOP BAR ===== */}
      <div className="top-bar">
        <button
          className="topbar-menu-btn"
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          aria-label="Toggle sidebar"
        >
          <FaBars />
        </button>

        <img src={logo} alt="Logo" className="topbar-logo" />

        <span className="topbar-page-title">
          {PAGE_TITLES[activeView] || ''}
        </span>

        <div className="topbar-right">
          <button
            className="topbar-avatar-btn"
            onClick={() => setShowUserDetails(!showUserDetails)}
            aria-label="User menu"
          >
            {userInitial}
          </button>
        </div>

        {/* User dropdown */}
        {showUserDetails && user && (
          <div className="user-details">
            <div className="user-card">
              <div className="user-card-header">
                <div className="user-avatar">{userInitial}</div>
                <div className="user-name">{user.name}</div>
                <div className="user-role">
                  {user.role === 'admin' ? 'Administrator' : 'Employee'}
                </div>
              </div>
              <div className="user-card-body">
                <div className="user-field">
                  <span className="user-label">Email</span>
                  <span className="user-value">{user.email || '—'}</span>
                </div>
                {user.role === 'admin' ? (
                  <div className="user-field">
                    <span className="user-label">Business ID</span>
                    <span className="user-value" title={user.id}>{user.id}</span>
                  </div>
                ) : (
                  <>
                    <div className="user-field">
                      <span className="user-label">Role</span>
                      <span className="user-value">Employee</span>
                    </div>
                    <div className="user-field">
                      <span className="user-label">Business</span>
                      <span className="user-value" title={user.business_id}>{user.business_id}</span>
                    </div>
                  </>
                )}
                <div className="user-card-actions">
                  <button
                    className="btn-primary"
                    onClick={() => handleNavigateView('profile')}
                  >
                    <FaEdit size={13} /> My Profile
                  </button>
                  <button className="btn-secondary" onClick={handleLogout}>
                    <FaSignOutAlt size={13} /> Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== SIDEBAR + CONTENT ===== */}
      <div className="main-layout">
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          activeView={activeView}
          setActiveView={(v) => {
            navigate(VIEW_TO_PATH[v] || '/dashboard');
            setShowUserDetails(false);
          }}
          user={user}
        />

        <div className={`content${isSidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
          {gcalMessage && (
            <div className={`gcal-banner ${gcalMessage.type}`}>
              {gcalMessage.text}
              <button className="gcal-banner-close" onClick={() => setGcalMessage(null)}>✕</button>
            </div>
          )}

          {needsBusinessDetail && (
            <>
              <div className="business-warning-banner">
                <p>⚠ Please complete your business details to use the platform fully.</p>
                <button onClick={() => navigate('/business_detail')}>Complete Now</button>
              </div>
              <div className="overlay-disable" />
            </>
          )}

          <div className={needsBusinessDetail ? 'blurred-content' : ''}>
            {renderView()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Index;
